import { CalendarRange } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { ListMyRequests } from '@cold-ambulance/client';
import {
  Card,
  CardContent,
  Skeleton,
  buttonVariants,
} from '@cold-ambulance/shadcn';
import {
  LocationLink,
  formatDate,
  sumEquipmentQuantities,
} from '@cold-ambulance/ui';

function toEquipmentArray(value: unknown) {
  return Array.isArray(value) ? value : [];
}

export function RequestCard({
  request,
}: {
  request: ListMyRequests['records'][number];
}) {
  const { t } = useTranslation();
  const requestTransitionName = `request-card-${request.id}`;

  return (
    <Card
      className="request-view-transition-card border-primary bg-[#E9F6ED] py-7"
      key={request.id}
      style={{
        viewTransitionName: requestTransitionName,
      }}
    >
      <CardContent className="flex h-full flex-col space-y-4">
        <h2 className="text-lg font-bold">{request.projectName}</h2>
        <LocationLink
          name={request.location.name}
          latitude={request.location.latitude}
          longitude={request.location.longitude}
          className="text-sm"
        />
        <p className="mt-1 flex items-center gap-2 text-sm">
          <CalendarRange size={20} />
          {formatDate(request.scheduleStartDate)} -{' '}
          {formatDate(request.scheduleEndDate)}
        </p>
        <div className="flex items-center justify-between rounded-lg bg-white px-4 py-3">
          <p>{t('requests.requiredService')}</p>
          <ul>
            {request.ambulanceServiceWithDriver.map((service) => {
              const total =
                service.type1Amount + service.type2Amount + service.type3Amount;
              const equipmentCount =
                sumEquipmentQuantities(
                  toEquipmentArray(service.type1Equipment),
                ) +
                sumEquipmentQuantities(
                  toEquipmentArray(service.type2Equipment),
                ) +
                sumEquipmentQuantities(
                  toEquipmentArray(service.type3Equipment),
                );
              return total > 0 ? (
                <li key={service.id} className="font-bold">
                  {total} {t('services.ambulanceWithDriver')}
                  {equipmentCount > 0 && (
                    <span className="text-muted-foreground ms-1 text-xs font-normal">
                      ({equipmentCount} {t('services.equipment')})
                    </span>
                  )}
                </li>
              ) : null;
            })}
            {request.ambulanceServiceWithoutDriver.map((service) => {
              const total =
                service.type1Amount + service.type2Amount + service.type3Amount;
              const equipmentCount =
                sumEquipmentQuantities(
                  toEquipmentArray(service.type1Equipment),
                ) +
                sumEquipmentQuantities(
                  toEquipmentArray(service.type2Equipment),
                ) +
                sumEquipmentQuantities(
                  toEquipmentArray(service.type3Equipment),
                );
              return total > 0 ? (
                <li key={service.id} className="font-bold">
                  {total} {t('services.ambulanceWithoutDriver')}
                  {equipmentCount > 0 && (
                    <span className="text-muted-foreground ms-1 text-xs font-normal">
                      ({equipmentCount} {t('services.equipment')})
                    </span>
                  )}
                </li>
              ) : null;
            })}
            {request.ambulance4x4WithDriver.map((service) => {
              const equipmentCount = sumEquipmentQuantities(
                toEquipmentArray(service.equipment),
              );
              return service.amount > 0 ? (
                <li key={service.id} className="font-bold">
                  {service.amount} {t('services.ambulance4x4WithDriver')}
                  {equipmentCount > 0 && (
                    <span className="text-muted-foreground ms-1 text-xs font-normal">
                      ({equipmentCount} {t('services.equipment')})
                    </span>
                  )}
                </li>
              ) : null;
            })}
            {request.ambulance4x4WithoutDriver.map((service) => {
              const equipmentCount = sumEquipmentQuantities(
                toEquipmentArray(service.equipment),
              );
              return service.amount > 0 ? (
                <li key={service.id} className="font-bold">
                  {service.amount} {t('services.ambulance4x4WithoutDriver')}
                  {equipmentCount > 0 && (
                    <span className="text-muted-foreground ms-1 text-xs font-normal">
                      ({equipmentCount} {t('services.equipment')})
                    </span>
                  )}
                </li>
              ) : null;
            })}
            {request.mobileClinicService.map((service) => {
              const equipmentCount = sumEquipmentQuantities(
                toEquipmentArray(service.equipment),
              );
              return service.amount > 0 ? (
                <li key={service.id} className="font-bold">
                  {service.amount}{' '}
                  {t(
                    service.type === 'ADVANCED'
                      ? 'services.mobileClinicAdvanced'
                      : 'services.mobileClinicBasic',
                  )}
                  {equipmentCount > 0 && (
                    <span className="text-muted-foreground ms-1 text-xs font-normal">
                      ({equipmentCount} {t('services.equipment')})
                    </span>
                  )}
                </li>
              ) : null;
            })}
            {(() => {
              const equipment = request.medicalEquipment as unknown as
                | { name: string; quantity: number }[]
                | undefined;
              const totalCount =
                equipment?.reduce((acc, item) => acc + item.quantity, 0) || 0;
              return totalCount > 0 ? (
                <li className="font-bold">
                  {totalCount} {t('services.medicalEquipment')}
                </li>
              ) : null;
            })()}
          </ul>
        </div>
        <Link
          to={`/requests/${request.id}`}
          viewTransition
          className={buttonVariants({
            variant: 'outline',
            className:
              'border-primary text-primary mt-auto h-11! w-full bg-white px-6 font-bold',
          })}
        >
          {t('common.viewDetails')}
        </Link>
      </CardContent>
    </Card>
  );
}

export function RequestCardSkeleton() {
  return (
    <div className="border-primary rounded-lg border p-6">
      <div className="space-y-4">
        <Skeleton className="h-7 w-3/4" />

        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-5 w-32" />
        </div>

        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-5 w-48" />
        </div>

        <div className="rounded-lg bg-white px-4 py-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-5 w-24" />
          </div>
        </div>

        <Skeleton className="mt-4 h-11 w-full rounded-md" />
      </div>
    </div>
  );
}
